/**
 * GSAP entrance and image parallax for overlapping image/content rows.
 */

class OverlapImageContentComponent extends HTMLElement {
  connectedCallback() {
    this.cleanup();
    this.loadGsap()
      .then(() => {
        if (this.isConnected) this.initAnimations();
      })
      .catch(() => {
        // Content remains fully visible if the optional animation runtime fails.
      });
  }

  disconnectedCallback() {
    this.cleanup();
  }

  loadGsap() {
    if (window.gsap && window.ScrollTrigger) return Promise.resolve();

    if (!window.AntaresGsapPromise) {
      const gsapReady = window.gsap
        ? Promise.resolve()
        : this.loadScript(this.dataset.gsapSrc);

      window.AntaresGsapPromise = gsapReady.then(() =>
        window.ScrollTrigger
          ? Promise.resolve()
          : this.loadScript(this.dataset.scrollTriggerSrc)
      );
    }

    return window.AntaresGsapPromise;
  }

  loadScript(src) {
    if (!src) return Promise.reject(new Error('Missing GSAP asset URL'));

    return new Promise((resolve, reject) => {
      const existing = document.querySelector(`script[src="${src}"]`);
      if (existing) {
        if (existing.dataset.loaded === 'true') {
          resolve();
          return;
        }
        existing.addEventListener('load', resolve, { once: true });
        existing.addEventListener('error', reject, { once: true });
        return;
      }

      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.addEventListener(
        'load',
        () => {
          script.dataset.loaded = 'true';
          resolve();
        },
        { once: true }
      );
      script.addEventListener('error', reject, { once: true });
      document.head.appendChild(script);
    });
  }

  initAnimations() {
    const gsap = window.gsap;
    const ScrollTrigger = window.ScrollTrigger;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!gsap || !ScrollTrigger || reducedMotion) return;

    gsap.registerPlugin(ScrollTrigger);

    const rows = Array.from(this.querySelectorAll('[data-overlap-row]'));
    const parallaxStrength = Number(this.dataset.parallaxStrength) || 0;

    this.gsapContext = gsap.context(() => {
      rows.forEach((row, index) => {
        const imageContainer = row.querySelector('[data-overlap-image]');
        const image = row.querySelector('.overlap-image-content-row__image');
        const content = row.querySelector('[data-overlap-content]');
        const imageOnRight = row.dataset.layout === 'image_right';
        const direction = imageOnRight ? 1 : -1;

        if (!imageContainer || !content) return;

        gsap.set([imageContainer, content], { autoAlpha: 0 });
        gsap.set(imageContainer, { x: direction * 36, y: 22 });
        gsap.set(content, { x: direction * -28, y: 30 });

        const timeline = gsap.timeline({
          delay: Math.min(index * 0.08, 0.24),
          defaults: {
            duration: 0.85,
            ease: 'power2.out',
          },
          scrollTrigger: {
            trigger: row,
            start: 'top 82%',
            once: true,
          },
        });

        timeline
          .to(imageContainer, {
            autoAlpha: 1,
            x: 0,
            y: 0,
          })
          .to(
            content,
            {
              autoAlpha: 1,
              x: 0,
              y: 0,
            },
            '-=0.58'
          );

        if (image && parallaxStrength > 0) {
          gsap.fromTo(
            image,
            { yPercent: parallaxStrength * -1 },
            {
              yPercent: parallaxStrength,
              ease: 'none',
              scrollTrigger: {
                trigger: row,
                start: 'top bottom',
                end: 'bottom top',
                scrub: 1,
              },
            }
          );
        }
      });
    }, this);

    ScrollTrigger.refresh();
  }

  cleanup() {
    this.gsapContext?.revert();
    this.gsapContext = null;
  }
}

if (!customElements.get('overlap-image-content-component')) {
  customElements.define('overlap-image-content-component', OverlapImageContentComponent);
}
